import { Calendar } from '@griply/assets';
import { CalendarRef } from '@griply/types';
import {
  canConnectionWriteEvents,
  getDefaultEventCalendar,
  getWritableConnectionCalendars,
} from '@griply/utils';
import { Typography } from '@mui/material';
import {
  useExternalCalendarConnections,
  useSetDefaultCalendar,
} from 'features/external-connections';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarSelect } from 'shared/components/ui/calendar-select';
import { Divider } from 'shared/components/ui/divider';
import { Icon } from 'shared/components/ui/icon';
import { InfoIcon } from 'shared/components/ui/info-icon';
import { useExternalConnectionsCalendarsQueries } from 'shared/hooks/queries/use-external-connections-calendars-queries';

import * as Styled from './calendar-default-settings.style';

export const CalendarDefaultSettings: React.FC = () => {
  const { t } = useTranslation();
  const connections = useExternalCalendarConnections();
  const setDefaultCalendar = useSetDefaultCalendar();

  const connectionIds = useMemo(
    () => connections.map(({ id }) => id),
    [connections],
  );
  const { calendars, isPending: isLoadingCalendars } =
    useExternalConnectionsCalendarsQueries(connectionIds);

  // Only a calendar an event can actually be written to can be the default — offering a
  // read-only one would store a target the create form silently falls back off of.
  const selectableCalendars = useMemo(
    () =>
      getWritableConnectionCalendars(connections, calendars, {
        requireCalendarWrite: true,
      }),
    [calendars, connections],
  );

  // The effective default, not the stored field: the row states where a new event actually
  // lands, which for a user who never picked one is the name-sorted first calendar.
  const value = useMemo<CalendarRef | null>(
    () => getDefaultEventCalendar(connections, selectableCalendars),
    [connections, selectableCalendars],
  );

  // Connection-level first: the per-calendar canWrite flags sit behind the calendars callables,
  // so until those land an empty selectableCalendars cannot be told apart from "not fetched yet"
  // and the section would flash out. Once they have landed, an account whose calendars are all
  // read-only hides it too — there is nothing the default could be set to.
  if (
    !connections.some(canConnectionWriteEvents) ||
    (!isLoadingCalendars && !selectableCalendars.length)
  ) {
    return null;
  }

  return (
    <Styled.Section>
      <Divider />

      <Typography variant="h6" component="h3">
        {t('calendar.default-calendar.title')}
      </Typography>

      <Styled.Body>
        <Styled.LabelCell>
          <Styled.Label>
            <Icon icon={Calendar} />
            {t('forms.event.fields.calendar.label')}
          </Styled.Label>
          <InfoIcon label={t('calendar.default-calendar.info')} />
        </Styled.LabelCell>
        <Styled.Field>
          <CalendarSelect
            connections={connections}
            calendars={calendars}
            selectableCalendars={selectableCalendars}
            value={value}
            isLoading={isLoadingCalendars}
            onChange={setDefaultCalendar}
          />
        </Styled.Field>
      </Styled.Body>
    </Styled.Section>
  );
};
